import { z } from "zod";
import { time24Schema } from "./common.js";

// =============================================================================
// Report schedule — when the automated reports are emailed.
//
// A super-admin configures ONE global send time for the weekly report and
// ONE for the monthly report. Both are interpreted in America/New_York
// (Eastern Time) for everyone — the same timezone every Sinai trip and
// every other report window is anchored to. Defaults are 06:00 ET, which
// is the historical behaviour, so nothing changes until an admin picks a
// different time.
//
// How the time actually takes effect:
//   The reports fire from Cloud Run Jobs triggered by Cloud Scheduler. A
//   fixed cron can't express an app-configurable time, so the scheduler
//   ticks FREQUENTLY (every 30 min on the relevant day) and each job
//   decides whether it's actually due based on the configured time + a
//   "already sent this period" marker. The due rule
//   ("current ET time >= configured time AND not yet sent this period")
//   is resilient to missed / late / repeated ticks and can never
//   double-send within a period. See `weeklyReportDue` / `monthlyReportDue`.
// =============================================================================

/** The historical send time both cadences default to (06:00 ET). */
export const DEFAULT_REPORT_SEND_TIME = "06:00";

/** Tick granularity the Cloud Scheduler is expected to fire at (minutes).
 *  The UI offers send times in this step so the configured time always
 *  lines up with a scheduler tick — a time of 06:30 fires at the 06:30
 *  tick; an off-grid time like 06:25 would simply fire at the next tick
 *  at or after it (06:30). Keep this in sync with the cron expressions in
 *  docs/DEPLOY.md. */
export const REPORT_TICK_MINUTES = 30;

export const reportScheduleSchema = z.object({
  /** HH:MM (24h, ET) daily-cadence reports are sent at, every morning.
   *
   *  OPTIONAL, and deliberately NOT defaulted. An API that predates the daily
   *  cadence omits it, and a default would have the client invent 06:00,
   *  display it as configured, and write that invented value back on the next
   *  save — silently moving a send time nobody chose to change. Undefined has
   *  to stay undefined so the client can hide the control instead. */
  dailySendTime: time24Schema.optional(),
  /** HH:MM (24h, ET) the weekly report is sent at, Mondays. */
  weeklySendTime: time24Schema,
  /** HH:MM (24h, ET) the monthly report is sent at, on the 1st. */
  monthlySendTime: time24Schema,
  /** ISO timestamp the weekly report last actually went out (the dedupe
   *  marker's stamp). Null until the first send under the new schedule. */
  lastWeeklySentAt: z.string().nullable(),
  /** ISO timestamp the monthly report last actually went out. */
  lastMonthlySentAt: z.string().nullable(),
});
export type ReportSchedule = z.infer<typeof reportScheduleSchema>;

export const reportScheduleResponseSchema = z.object({
  schedule: reportScheduleSchema,
});
export type ReportScheduleResponse = z.infer<typeof reportScheduleResponseSchema>;

/** PUT body: just the two configurable times. The last-sent markers are
 *  owned by the jobs, never by the client. */
export const reportScheduleUpdateRequestSchema = z.object({
  /** Optional so a client that predates the daily cadence cannot blank it:
   *  absent means "leave the daily time as it is", never "reset to default".
   *  An omitted field must not silently change when a report goes out. */
  dailySendTime: time24Schema.optional(),
  weeklySendTime: time24Schema,
  monthlySendTime: time24Schema,
});
export type ReportScheduleUpdateRequest = z.infer<
  typeof reportScheduleUpdateRequestSchema
>;

// =============================================================================
// Pure due-check logic (timezone-aware, no DB) — unit-tested in
// reportSchedule.test.ts. Kept here in @mtform/shared so both the jobs
// and the tests share one implementation of the period / time math.
// =============================================================================

/** A single instant decomposed into the America/New_York wall-clock
 *  facts the due-check needs. Computed with Intl so DST is handled by
 *  the platform tz database rather than hand-rolled offsets. */
export interface NyMoment {
  /** Calendar date in ET, `YYYY-MM-DD`. */
  dateIso: string;
  /** ISO weekday in ET: 1 = Monday … 7 = Sunday. */
  isoWeekday: number;
  /** Wall-clock time in ET, `HH:MM` (24h, zero-padded). */
  clock: string;
}

const NY_TZ = "America/New_York";
const ISO_WEEKDAY_BY_SHORT: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

/** Decompose an instant into its America/New_York wall-clock parts.
 *  `hourCycle: "h23"` avoids the locale quirk where midnight formats as
 *  "24:00", so `clock` is always 00:00–23:59 and string-comparable. */
export function nyMoment(d: Date = new Date()): NyMoment {
  const dateIso = new Intl.DateTimeFormat("en-CA", {
    timeZone: NY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  const clock = new Intl.DateTimeFormat("en-GB", {
    timeZone: NY_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
  const weekdayShort = new Intl.DateTimeFormat("en-US", {
    timeZone: NY_TZ,
    weekday: "short",
  }).format(d);
  return {
    dateIso,
    isoWeekday: ISO_WEEKDAY_BY_SHORT[weekdayShort] ?? 0,
    clock,
  };
}

export interface ReportDueInput {
  /** The current instant, decomposed into ET parts. */
  moment: NyMoment;
  /** Configured send time, `HH:MM` (ET). */
  sendTime: string;
  /** The period key the report was last sent for ("" if never).
   *  Compared against the current period's key to dedupe. */
  lastSentPeriod: string;
}

export type ReportNotDueReason =
  | "wrong_day"
  | "before_time"
  | "already_sent";

export interface ReportDueResult {
  /** Whether the report should be sent on this tick. */
  due: boolean;
  /** Stable identifier for the current period. Stamp this as the
   *  last-sent marker after a successful send so later ticks in the same
   *  period no-op. Weekly = the Monday's ET date (`YYYY-MM-DD`, which
   *  uniquely identifies the ISO week for a Monday-fired report);
   *  Monthly = the ET year-month (`YYYY-MM`). */
  periodKey: string;
  /** Why it's not due, when `due` is false — surfaced in the job logs. */
  reason: ReportNotDueReason | "due";
}

/** Weekly report (Mondays) due-check. Due iff: today is Monday in ET,
 *  the current ET time is at/after the configured send time, and the
 *  current ISO week hasn't already been sent. */
export function weeklyReportDue(input: ReportDueInput): ReportDueResult {
  const { moment, sendTime, lastSentPeriod } = input;
  const periodKey = moment.dateIso;
  if (moment.isoWeekday !== 1) {
    return { due: false, periodKey, reason: "wrong_day" };
  }
  if (moment.clock < sendTime) {
    return { due: false, periodKey, reason: "before_time" };
  }
  if (lastSentPeriod === periodKey) {
    return { due: false, periodKey, reason: "already_sent" };
  }
  return { due: true, periodKey, reason: "due" };
}

/** Monthly report (1st of the month) due-check. Due iff: today is the
 *  1st in ET, the current ET time is at/after the configured send time,
 *  and the current calendar month hasn't already been sent. */
export function monthlyReportDue(input: ReportDueInput): ReportDueResult {
  const { moment, sendTime, lastSentPeriod } = input;
  // `YYYY-MM` — the month being processed (the 1st's own month).
  const periodKey = moment.dateIso.slice(0, 7);
  const dayOfMonth = moment.dateIso.slice(8, 10);
  if (dayOfMonth !== "01") {
    return { due: false, periodKey, reason: "wrong_day" };
  }
  if (moment.clock < sendTime) {
    return { due: false, periodKey, reason: "before_time" };
  }
  if (lastSentPeriod === periodKey) {
    return { due: false, periodKey, reason: "already_sent" };
  }
  return { due: true, periodKey, reason: "due" };
}
